import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface InsightDetails {
  explanation: string;
  relatedConcepts?: string[];
  references?: { title: string; url?: string }[];
  example?: string;
  quiz?: {
    question: string;
    options: string[];
    correctIndex: number;
    explanation: string;
  };
}

interface InteractiveInsightCardProps {
  title: string;
  value?: string;
  label?: string;
  sublabel?: string;
  description?: string;
  icon?: string;
  color: string;
  borderColor?: string;
  details: InsightDetails;
  className?: string;
}

export function InteractiveInsightCard({
  title,
  value,
  label,
  sublabel,
  description,
  icon,
  color,
  borderColor,
  details,
  className = '',
}: InteractiveInsightCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);

  const handleClick = useCallback(() => {
    setIsExpanded(!isExpanded);
    setShowQuiz(false);
    setSelectedAnswer(null);
    setShowResult(false);
  }, [isExpanded]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  const handleQuizAnswer = useCallback((index: number) => {
    setSelectedAnswer(index);
    setShowResult(true);
  }, []);

  const colorClasses: Record<string, { text: string; bg: string; border: string; hoverBorder: string }> = {
    violet: { text: 'text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-400/20', hoverBorder: 'hover:border-violet-400/50' },
    indigo: { text: 'text-indigo-400', bg: 'bg-indigo-500/10', border: 'border-indigo-400/20', hoverBorder: 'hover:border-indigo-400/50' },
    amber: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-400/20', hoverBorder: 'hover:border-amber-400/50' },
    rose: { text: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-400/20', hoverBorder: 'hover:border-rose-400/50' },
    emerald: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-400/20', hoverBorder: 'hover:border-emerald-400/50' },
    cyan: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-400/20', hoverBorder: 'hover:border-cyan-400/50' },
    purple: { text: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-400/20', hoverBorder: 'hover:border-purple-400/50' },
    teal: { text: 'text-teal-400', bg: 'bg-teal-500/10', border: 'border-teal-400/20', hoverBorder: 'hover:border-teal-400/50' },
    blue: { text: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-400/20', hoverBorder: 'hover:border-blue-400/50' },
  };

  const colors = colorClasses[color] || colorClasses.violet;

  return (
    <motion.div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      className={`p-4 rounded-xl border bg-slate-900/60 cursor-pointer transition-all duration-200 ${borderColor || colors.border} ${colors.hoverBorder} focus:outline-none focus:ring-2 focus:ring-${color}-400/50 ${className}`}
      aria-expanded={isExpanded}
      aria-label={`${title}. Click to ${isExpanded ? 'collapse' : 'expand'} for more details.`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {value && (
            <div className={`text-2xl sm:text-3xl font-bold ${colors.text}`}>{value}</div>
          )}
          {icon && !value && (
            <span className="text-2xl block mb-2">{icon}</span>
          )}
          {label && (
            <div className="text-xs text-slate-300 mt-1 font-medium">{label}</div>
          )}
          {sublabel && (
            <div className="text-[10px] text-slate-500">{sublabel}</div>
          )}
          {title && !label && (
            <div className={`text-sm font-semibold ${colors.text} mb-1`}>{title}</div>
          )}
          {description && (
            <p className="text-xs text-slate-400 leading-relaxed">{description}</p>
          )}
        </div>
        <motion.span
          animate={{ rotate: isExpanded ? 180 : 0 }}
          className={`text-slate-500 text-sm ml-2 flex-shrink-0 ${isExpanded ? colors.text : ''}`}
        >
          {'▼'}
        </motion.span>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className={`mt-4 pt-4 border-t ${colors.border}`}>
              <div className={`p-3 rounded-lg ${colors.bg} mb-3`}>
                <div className={`text-xs font-semibold ${colors.text} mb-1`}>Learn More</div>
                <p className="text-xs text-slate-300 leading-relaxed">{details.explanation}</p>
              </div>

              {details.example && (
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 mb-3">
                  <div className="text-xs font-semibold text-slate-400 mb-1">Example</div>
                  <p className="text-xs text-slate-300 font-mono">{details.example}</p>
                </div>
              )}

              {details.relatedConcepts && details.relatedConcepts.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-semibold text-slate-400 mb-2">Related Concepts</div>
                  <div className="flex flex-wrap gap-2">
                    {details.relatedConcepts.map((concept) => (
                      <span key={concept} className={`px-2 py-1 text-[10px] rounded-full ${colors.bg} ${colors.text} border ${colors.border}`}>
                        {concept}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {details.references && details.references.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-semibold text-slate-400 mb-2">References</div>
                  <ul className="space-y-1">
                    {details.references.map((ref) => (
                      <li key={ref.title} className="text-[10px] text-slate-500">
                        {ref.url ? (
                          <a href={ref.url} target="_blank" rel="noopener noreferrer" className={`${colors.text} hover:underline`} onClick={(e) => e.stopPropagation()}>
                            {ref.title}
                          </a>
                        ) : (
                          <span>{ref.title}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {details.quiz && (
                <div className="mt-3">
                  {!showQuiz ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowQuiz(true); }}
                      className={`w-full px-3 py-2 text-xs font-semibold rounded transition-all ${colors.bg} ${colors.text} border ${colors.border} hover:bg-opacity-80`}
                    >
                      Test Your Knowledge
                    </button>
                  ) : (
                    <div className="p-3 rounded-lg bg-slate-800/70 border border-slate-700">
                      <div className="text-xs font-semibold text-slate-300 mb-3">{details.quiz.question}</div>
                      <div className="space-y-2">
                        {details.quiz.options.map((option, index) => (
                          <button
                            key={index}
                            onClick={(e) => { e.stopPropagation(); handleQuizAnswer(index); }}
                            disabled={showResult}
                            className={`w-full text-left px-3 py-2 text-xs rounded transition-all ${
                              showResult
                                ? index === details.quiz!.correctIndex
                                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-400/30'
                                  : selectedAnswer === index
                                  ? 'bg-rose-500/20 text-rose-400 border border-rose-400/30'
                                  : 'bg-slate-700/50 text-slate-400 border border-slate-600'
                                : 'bg-slate-700/50 text-slate-300 border border-slate-600 hover:bg-slate-700 hover:border-slate-500'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                      {showResult && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`mt-3 p-2 rounded text-xs ${
                            selectedAnswer === details.quiz.correctIndex
                              ? 'bg-emerald-500/10 text-emerald-400'
                              : 'bg-rose-500/10 text-rose-400'
                          }`}
                        >
                          {selectedAnswer === details.quiz.correctIndex ? 'Correct! ' : 'Not quite. '}
                          {details.quiz.explanation}
                        </motion.div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-3 text-center">
                <span className="text-[10px] text-slate-600">Click to collapse</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
